'use strict';

const _ = require('underscore');
const Backbone = require('backbone');
const Page = require('framework/domain/value/page');
const Pagination = require('framework/domain/value/pagination');
const ReadMore = require('framework/domain/value/readmore');

module.exports = class ReadMoreCollection extends Backbone.Collection {
    /**
     * 「ReadMore」で続きを読み込むタイプの Collection
     *
     * `model2number` で Model を数直線状にマッピングし、
     * 各 Page に含まれる Model の範囲を明確にすることで、
     * 同時編集などで不用意に本来見れないはずの Model が見えてしまうのを防ぐ。
     *
     * また、複数の Page を同時に保持できるため、最初に読み込んだ後、通知などで、
     * 途中の Model を読み込んでも、間があることを示したり、さらに読むなども適切に表示できる。
     *
     * @param models {Backbone.Model[]}
     */
    constructor(models, options) {
        super(models, options);

        this._totalCount = null;
        this._pages = [];
        this._firstFetched = false;

        this.on('readmore:next', this.fetchNextPage);
        this.on('readmore:prev', this.fetchPrevPage);
        this.on('change:value', this.sort);
    }

    comparator(a, b) {
        if(a instanceof ReadMore && b instanceof ReadMore) {
            return a.value - b.value;
        }

        if(a instanceof ReadMore) {
            return a.value - this.model2number(b);
        }

        if(b instanceof ReadMore) {
            return this.model2number(a) -  b.value;
        }

        return this.model2number(a) - this.model2number(b);
    }

    /**
     * Model を数直線上の一点に射影することで、ソートだけでなく範囲を指定できるようにする
     *
     * 具体的な Collection ごとに異なるため、具象クラスで実装する必要がある。
     *
     * @param model
     */
    model2number(model) {
        throw new Error('Not Implemented!');
    }

    /**
     * この Collection に潜在的に含まれている Model の数を返す
     *
     * まだ、その数を取得できていない場合は null が返る。
     *
     * @returns {number|null}
     */
    getTotalCount() {
        return this._totalCount;
    }

    addTotalCount(num) {
        this._totalCount += num;
        this.trigger('change:totalCount');
    }

    _addPage(page) {
        this._pages.push(page);
    }

    /**
     * Page object を生成する
     *
     * @param models {Backbone.Model[]}
     * @param pagination {Pagination}
     * @param isFirstFetch {boolean}
     * @returns {Page}
     */
    buildPage(models, pagination, isFirstFetch=false) {
        const numbers = _.map(models, this.model2number);
        const min = isFirstFetch ? -Infinity : _.min(numbers) - 0.1;
        const max = numbers.length > 0 ? _.max(numbers) + 0.1 : Infinity;

        return new Page(
            min, pagination.prevCursor, pagination.prevMore,
            max, pagination.nextCursor, pagination.nextMore
        );
    }

    /**
     * Repository から Model を fetch する
     *
     * @param limit
     * @param cursor
     * @param isAsc: true ならば昇順で false ならば降順で取得する。
     * @returns fetch した model の list と pagination object を以下の形式で返す
     * {
     *     models: models,
     *     pagination: pagination
     * }
     */
    fetch(limit, cursor, isAsc=true) {
        throw new Error('Not Implemented');
    }

    fetchFirstPage(limit=3) {
        if(this._firstFetched) {
            return new Promise((resolve, reject) => {
                resolve(this);
            });
        }

        return this.fetch(limit, null, true)
            .then(result => {
                this.add(result.models, {merge: true});
                this._totalCount = result.pagination.totalCount;

                const page = this.buildPage(result.models, result.pagination, true);
                this._addPage(page);
                this.add(page.getReadMores());

                this._firstFetched = true;

                return this;
            });
    }

    buildEmptyPage() {
        this._totalCount = 0;
        const pagination = new Pagination(this._totalCount);
        const page = this.buildPage([], pagination, true);
        this._addPage(page);
        this.add(page.getReadMores());

        this._firstFetched = true;

        return new Promise((resolve, reject) => {
            resolve(this);
        });
    }

    fetchNextPage(limit, page) {
        return this.fetch(limit, page.nextCursor, true)
            .then(result => {
                this.add(result.models, {merge: true});

                const nextPage = this.buildPage(result.models, result.pagination);
                page.mergeNext(nextPage);

                return this;
            });
    }

    fetchPrevPage(limit, page) {
        return this.fetch(limit, page.prevCursor, false)
            .then(result => {
                this.add(result.models, {merge: true});

                const prevPage = this.buildPage(result.models, result.pagination);
                page.mergeNext(prevPage);

                return this;
            });
    }

    isBelongToSomePage(model) {
        if(!this.isBelongToCollection(model)) {
            return;
        }

        const num = this.model2number(model);

        return _.some(_.map(this._pages, p => p.include(num)));
    }

    isBelongToCollection(model) {
        throw new Error('Not Implemented');
    }
};



// WEBPACK FOOTER //
// ./framework/domain/collection/readmore-collection.js